import { getHistoryInstance, getLocationUrl } from 'hooks/useHistory'
import { action, computed, makeAutoObservable, observable } from 'mobx'
import React from 'react'
import { ourUuid } from 'utils'
import { getOrigin } from 'utils/location'

export const UNKNOWN_URL = 'unknown'

export interface TabEntry {
  readonly initialUrl: string
  href: string
  title: string
  readonly uuid: string
  isLoading: boolean
  history: {
    canGoBack: boolean
    canGoForward: boolean
  }
}

export class TabRootStore {
  currentTab: TabEntry
  tabs: TabEntry[] = [
    {
      initialUrl: getLocationUrl().href,
      href: getLocationUrl().href,
      title: window.document.title,
      uuid: ourUuid(),
      isLoading: true,
      history: { canGoBack: false, canGoForward: false },
    },
  ]
  constructor() {
    makeAutoObservable(this, {
      tabs: observable,
      currentTab: observable,
      canCloseTabs: computed,
      setCurrentTab: action,
      closeTab: action,
      addTab: action,
      updateTabEntry: action,
      movePosition: action,
    })
    this.currentTab = this.tabs[0]
  }
  get canCloseTabs() {
    return this.tabs.length > 1
  }
  setCurrentTab(tab: TabEntry | number) {
    if (typeof tab === 'number') {
      if (tab < 0 || tab >= this.tabs.length) return
      tab = this.tabs[tab]
    }
    this.currentTab = tab
    try {
      const url = new URL(tab.href)
      if (url.origin === getOrigin()) {
        getHistoryInstance().replace(url.pathname + url.search + url.hash)
      }
    } catch {}
  }
  closeTab(tab: TabEntry) {
    if (this.tabs.length === 1) {
      return
    }
    const tabIndex = this.tabs.indexOf(tab)
    if (tabIndex === -1) {
      return
    }
    this.tabs.splice(tabIndex, 1)
    if (tab !== this.currentTab) {
      return
    }
    if (this.tabs.length > tabIndex) {
      this.currentTab = this.tabs[tabIndex]
    } else {
      this.currentTab = this.tabs[tabIndex - 1]
    }
  }
  closeCurrentTab() {
    this.closeTab(this.currentTab)
  }
  addTab(url: string, title: string = 'New tab', switchToTab = false) {
    const newTab: TabEntry = {
      initialUrl: url,
      href: url,
      title,
      uuid: ourUuid(),
      isLoading: true,
      history: { canGoBack: false, canGoForward: false },
    }
    makeAutoObservable(newTab)
    this.tabs.push(newTab)
    if (switchToTab) {
      this.setCurrentTab(newTab)
    }
  }
  updateTabEntry(tabEntry: TabEntry, updates: Partial<TabEntry>) {
    Object.assign(tabEntry, updates)
  }
  movePosition(tabEntry: TabEntry, newIndex: number) {
    const curIndex = this.tabs.indexOf(tabEntry)
    this.tabs.splice(curIndex, 1)
    this.tabs.splice(newIndex, 0, tabEntry)
  }
  goToNextOrPreviousTab(nextTab: boolean) {
    let index = this.tabs.indexOf(this.currentTab)
    index += nextTab ? 1 : this.tabs.length - 1
    index %= this.tabs.length
    this.setCurrentTab(index)
  }
}

export const TabRootContext = React.createContext<TabRootStore | null>(null)

export const TabRootProvider = ({ children }: { children: React.ReactNode }) => {
  const store = React.useMemo(() => new TabRootStore(), [])
  return <TabRootContext.Provider value={store}>{children}</TabRootContext.Provider>
}

export const useTabRoot = () => {
  const context = React.useContext(TabRootContext)
  if (context == null) {
    throw new Error('useTabRoot must be within TabRootContext')
  }

  return context
}

export const useOptionalTabRoot = () => {
  const context = React.useContext(TabRootContext)

  return context
}
